import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import type { BookingData } from '@/types/booked-bet';
import type { SmpTransactionTicket } from '@nf/types/ticket';
import type { FreeBetVoucherConditionForValidation } from '@/features/betslip/voucher/hooks/use-free-bet-voucher';
import { VoucherSystemVersion } from '@nf/achievement-system-v2';
import { not } from '@nf/utils-common/fpw/libs';
import { BetslipService } from '@/services/betslip.service';
import { smpService } from '@/features/single-match-parlay/services/smp.service';
import {
	BetslipType,
	ParlayTicketModel,
	ParlayTicketsModel,
	TicketModel,
	BetTicketModel,
	BetParlayTicketsModel,
	OddsTicketModel,
	BetMsgModel,
	CanBetResultModel,
	BETTING_CODE,
	VoucherType,
	BetAction,
	BetSourceType,
	NormalBetslipTab,
	ChildTabType,
	selectTicketMaxLength,
	selectTicketGroupMaxLengthBBPlus,
	UserBetSlipTab,
} from '@/hooks/query';
import { ViewEnum } from '@/types';
import { BetDefaultStakeType } from '@/services/account.service';
import { removeCommas } from '@/utils/odds-utils';
import { sportService } from '@/services/sport.service';
import { bbPlusService } from '@/services/bbplus.service';

type TicketCartsType = 'normal' | 'bb-plus';

type TicketCart = {
	multiTickets: Array<TicketModel>;
	parlayTicketModel: ParlayTicketsModel | null;
	oddsPromotionCode?: string;
	canBetResult: CanBetResultModel;
	// UI / local state that differs between normal vs bb-plus cart
	selectAllOddsKey: string;
	focusOddsKey: string;
	multiStakePerBet: number;
	parlayStakePerBet: number;
	token: string;
	custId: number;
	extendRoyaleToken: string;
	language: string;
	sportType: number;
};

type TicketCarts = Record<TicketCartsType, TicketCart>;

type TicketCartPatch = Partial<TicketCart>;


export interface BetslipStore {
	betslipKey: string;
	betslipType: BetslipType;
	sourceStorage: Map<BetSourceType, Map<ChildTabType, Array<string>>>;
	multiStorage: Map<string, Array<TicketModel>>;
	parlayStorage: Map<string, ParlayTicketsModel>;
	tooltipMap: Map<string, Record<string, { value?: string | number }>>;
	ticketCarts: TicketCarts;
	isParlayMode: boolean;
	bookingData: BookingData | null;
	isBookingBet: boolean;
	isConfirmBet: boolean;
	isQuickBetSettingModalOpen: boolean;
	isQuickbetExpand: boolean;
	isExpand: boolean;
	isQuickBet: boolean;
	quickBetTicket: TicketModel | null;
	useVoucher: VoucherType;
	selectedFreeBetVoucherQuota: number | null;
	selectedFreeBetVoucherId: string | null;
	selectedFreeBetVoucherSystemVersion: number;
	selectedFreeBetCondition: FreeBetVoucherConditionForValidation | null;
	freeBetRemovedByInvalidOddsTooltipOpen: boolean;
	showFreeBetOddsConversionTooltip: boolean;
	preFreebetVoucherId: string | null;
	useFreeRiskFreeVoucher: boolean;
	useSabaCoin: boolean;
	betAction: number;
	enableQuickBet: boolean;
	betslipTab: NormalBetslipTab;
	betTimer: number;
	betbyTicketKeys: Set<string>;
	isBBPlusBetSlipMode: boolean; // BB plus 為了不影響舊的流程 開新的值為最高層級
	setIsBBPlusBetSlipMode: (value: boolean) => void;
	setBetslipType: (type: BetslipType) => void;
	clearTooltip: (key?: string) => void;
	onOddsButtonClick: (type: 'normal' | 'bb-plus', model: OddsTicketModel, oddsKey: string, childTab: ChildTabType, sourceType: BetSourceType, isParlayMode: boolean) => string | undefined;
	onSmpTrigger: (ticket: TicketModel) => string | undefined;
	setIsParlayMode: (value: boolean) => void;
	setIsBookingBet: (value: boolean) => void;
	toggleBookingBet: () => void;
	setBookingData: (data: BookingData) => void,
	setIsConfirmBet: (value: boolean) => void;
	setBetslipKey: (key: string) => void;
	getBetslipType: (type: 'normal' | 'bb-plus') => BetslipType;
	checkCanBet: (type: 'normal' | 'bb-plus', hasClickBet: boolean, doubleBetMsg?: string) => void;
	getMsgByTickets: (type: 'normal' | 'bb-plus') => BetMsgModel[];
	getTotalBet: (type: 'normal' | 'bb-plus', isIndependentMode: boolean, isConfirmBet?: boolean) => number;
	getTotalStake: (type: 'normal' | 'bb-plus', isIndependentMode: boolean, isConfirmBet?: boolean) => number;
	getTotalPayout: (type: 'normal' | 'bb-plus', isIndependentMode: boolean) => number;
	getTicketByKey: (type: 'normal' | 'bb-plus', key: string, isParlay: boolean) => TicketModel | ParlayTicketModel | undefined;
	updateTicket: (type: 'normal' | 'bb-plus', value: TicketModel | ParlayTicketModel, isParlay: boolean, oddsType?: number) => void;
	updateMultiTicket: (type: 'normal' | 'bb-plus', value: TicketModel, oddsType: number) => void;
	updateParlayItemTicket: (type: 'normal' | 'bb-plus', value: TicketModel) => void;
	updateParlayTicket: (type: 'normal' | 'bb-plus', value: ParlayTicketModel) => void;
	removeTicket: (type: 'normal' | 'bb-plus', oddsKey: string | undefined, childTab: ChildTabType, sourceType?: BetSourceType) => void;
	removeAllCartTicket: () => void;
	removeAllTicket: (type: 'normal' | 'bb-plus') => void;
	syncTicket: (type: 'normal' | 'bb-plus', multiTickets: Array<TicketModel>, parlayData: ParlayTicketsModel, oddsType: number, view: number, oddsPromotionCode?: string) => void;
	syncBetTicket: (type: 'normal' | 'bb-plus', multiTickets: Array<BetTicketModel>, parlayData: BetParlayTicketsModel, oddsType: number, view: number, oddsPromotionCode?: string) => void;
	syncSmpTicket: (betTicket: SmpTransactionTicket) => void;
	checkStake: (type: 'normal' | 'bb-plus', isIndependentMode: boolean, oddsType: number) => boolean;
	betAgain: (type: 'normal' | 'bb-plus', oddsType: number, defaultStakeId: string, InpbetStake: string, view: number) => void;
	setIsExpand: (type: 'normal' | 'bb-plus', value: boolean) => void,
	toggleIsExpand: (type: 'normal' | 'bb-plus') => void;
	setIsQuickBet: (value: boolean) => void;
	setIsQuickBetSettingModalOpen: (value: boolean) => void;
	toggleIsQuickBet: () => void;
	toggleIsQuickBetExpand: () => void;
	setFocusOddsKey: (type: 'normal' | 'bb-plus', value: string) => void;
	setSelectAllOddsKey: (type: 'normal' | 'bb-plus', value: string) => void;
	setMultiStakePerBet: (type: 'normal' | 'bb-plus', value: number) => void;
	setParlayStakePerBet: (type: 'normal' | 'bb-plus', value: number) => void;
	setQuickBetTicket: (model: OddsTicketModel) => string | undefined;
	clearQuickBetTicket: () => void;
	setVoucher: (voucherType: VoucherType) => void;
	removeFreeBetForInvalidOdds: () => void;
	setFreeBetRemovedByInvalidOddsTooltipOpen: (open: boolean) => void;
	setShowFreeBetOddsConversionTooltip: (show: boolean) => void;
	setSelectedFreeBetVoucher: (quota: number, voucherId: string, voucherSystemVersion: number) => void;
	setSelectedFreeBetCondition: (condition: FreeBetVoucherConditionForValidation | null) => void;
	setUseFreeRiskFreeVocher: (value: boolean) => void;
	setUseSabaCoin: (value: boolean) => void;
	setBetAction: (betAction: number) => void;
	setSourceStorage: (sourceStorage: Map<BetSourceType, Map<ChildTabType, Array<string>>>) => void;
	setEnableQuickBet: (value: boolean) => void;
	setBetslipTab: (value: NormalBetslipTab) => void;
	setBetTimer: (value: number) => void;
	toggleBetbyTicketKey: (oddsKey: string) => void;
	clearBetbyTicketKeys: () => void;
	switchToUserBetSlipTab: (tabs: UserBetSlipTab) => void;
	getTicketCarts: (type: TicketCartsType) => TicketCarts[TicketCartsType];
	setTicketCarts: (type: TicketCartsType, patch: TicketCartPatch) => void;
}

export const useBetslipStore = create<BetslipStore, [['zustand/immer', never]]>(
	immer<BetslipStore>((set, get) => ({
		betslipKey: 'all', // 用於區分 Sports 及 VirtualSports, 待確認 VS 是否要再細分暫存
		betslipType: 'empty',
		sourceStorage: new Map<BetSourceType, Map<ChildTabType, Array<string>>>(),
		multiStorage: new Map<string, Array<TicketModel>>(),
		parlayStorage: new Map<string, ParlayTicketsModel>(),
		ticketCarts: {
			'normal': {
				multiTickets: [],
				parlayTicketModel: null,
				canBetResult: { innerMsg: [], outerMsg: [], canBet: true, confirmBetMsg: [] },
				selectAllOddsKey: '',
				focusOddsKey: '',
				multiStakePerBet: 0,
				parlayStakePerBet: 0,
				thirdPartyBetLists: [],
				token: '',
				custId: 0,
				extendRoyaleToken: '',
				language: '',
				sportType: 0,
			},
			'bb-plus': {
				multiTickets: [],
				parlayTicketModel: null,
				canBetResult: { innerMsg: [], outerMsg: [], canBet: true, confirmBetMsg: [] },
				selectAllOddsKey: '',
				focusOddsKey: '',
				multiStakePerBet: 0,
				parlayStakePerBet: 0,
				thirdPartyBetLists: [],
				token: '',
				custId: 0,
				extendRoyaleToken: '',
				language: '',
				sportType: 0,
			},
		},
		tooltipMap: new Map<string, Record<string, { value?: string | number }>>(),
		bookingData: null,
		isBookingBet: false,
		isExpand: false,
		isQuickBet: false,
		isQuickbetExpand: false,
		isParlayMode: false,
		isConfirmBet: false,
		isQuickBetSettingModalOpen: false,
		quickBetTicket: null,
		useVoucher: 'None',
		selectedFreeBetVoucherQuota: null,
		selectedFreeBetVoucherId: null,
		selectedFreeBetVoucherSystemVersion: VoucherSystemVersion.Original,
		selectedFreeBetCondition: null,
		freeBetRemovedByInvalidOddsTooltipOpen: false,
		showFreeBetOddsConversionTooltip: false,
		preFreebetVoucherId: null,
		useFreeRiskFreeVoucher: false,
		useSabaCoin: false,
		betAction: 0,
		enableQuickBet: true,
		betslipTab: 'single',
		betTimer: 0,
		betbyTicketKeys: new Set<string>(),
		isBBPlusBetSlipMode: false,
		isThirdPartyBetSlipMode: false,
		getTicketCarts: (type: TicketCartsType) => get().ticketCarts[type],
		setTicketCarts: (type: TicketCartsType, patch: TicketCartPatch) => {
			set((state) => {
				// Only update fields you actually pass (ignore `undefined`).
				(Object.keys(patch) as Array<keyof TicketCart>).forEach((key) => {
					const nextValue = patch[key];
					if (nextValue !== undefined) {
						state.ticketCarts[type][key] = nextValue as never;
					}
				});
			});
		},
		setIsBBPlusBetSlipMode: (value: boolean) => set({ isBBPlusBetSlipMode: value }),
		setBetslipType: (type: BetslipType) => set({ betslipType: type }),
		clearTooltip: (key?: string) => {
			set((state) => {
				if (key) {
					if (!state.tooltipMap.has(key)) return;
					const next = new Map(state.tooltipMap);
					next.delete(key);
					state.tooltipMap = next;
				} else {
					if (state.tooltipMap.size === 0) return;
					state.tooltipMap = new Map();
				}
			});
		},
		syncTicket: (type: 'normal' | 'bb-plus', multiTickets: Array<TicketModel>, parlayData: ParlayTicketsModel, oddsType: number, view: number, oddsPromotionCode?: string) => {

			// multi bet
			const tickets = get().ticketCarts[type].multiTickets;
			const useSabaCoin = get().useSabaCoin;
			const newTickets = BetslipService.patchSyncedMultiTickets(
				tickets,
				multiTickets,
				parlayData,
				oddsType,
				type === 'bb-plus',
				useSabaCoin,
				get().useVoucher
			);
			// parlay
			const parlayTicket = get().ticketCarts[type].parlayTicketModel;
			const newParlayData = BetslipService.patchSyncedParlayData(parlayData, parlayTicket, view);
			get().setTicketCarts(type, { multiTickets: newTickets, parlayTicketModel: newParlayData, oddsPromotionCode: oddsPromotionCode });
			get().checkCanBet(type, false);
		},
		syncBetTicket: (type: 'normal' | 'bb-plus', multiTickets: Array<BetTicketModel>, parlayData: BetParlayTicketsModel, oddsType: number, view: number, oddsPromotionCode?: string) => {
			// QuickBet 模式: 若背景仍有在 Betslip 送出下注的票, 收到 api res 後應直接清掉
			if (get().isQuickBet) {
				get().removeAllTicket(type);
				return;
			}

			const useSabaCoin = get().useSabaCoin;
			// multi bet
			const tickets = get().ticketCarts[type].multiTickets;
			const newTickets = BetslipService.patchBetSyncedMultiTickets(tickets, multiTickets, oddsType, useSabaCoin, type, get().useVoucher);
			
			// parlay
			const parlayTicket = get().ticketCarts[type].parlayTicketModel;

			const newParlayData = BetslipService.patchBetSyncedParlayData(
				parlayTicket,
				parlayData,
				newTickets,
				multiTickets,
				oddsType,
				view,
				useSabaCoin
			);
			// 如果double bet legodds會回傳空值 所以把舊的重新帶入 避免child match 失效
			if (type === 'bb-plus' && newParlayData && !newParlayData.LegOdds?.length) {
				newParlayData.LegOdds = parlayTicket?.LegOdds || [];
			}
			get().setTicketCarts(type, { multiTickets: newTickets, parlayTicketModel: newParlayData, oddsPromotionCode: oddsPromotionCode });

			// 因 Betting Code 65 回傳資料極少, 無法與 ticket mapping, 故額外取得
			const doubleBetMsg = multiTickets?.find(multiTicket => multiTicket.Code === 65)?.Message;
			get().checkCanBet(type, true, doubleBetMsg);
		},
		// smp ticket 只會加入normal
		syncSmpTicket: (betTicket: SmpTransactionTicket) => {
			const tickets =  get().ticketCarts['normal'].multiTickets;
			const smpTicket = tickets[0];
			const newTicket: TicketModel = {
				...smpTicket,
				CheckWaitingTicket: betTicket.CheckWaitingTicket,
				BetDelaySec: betTicket.BetDelaySec,
				ACCode: betTicket.ACCode,
				Code: betTicket.Code,
				Common: betTicket.Common,
				TransId: betTicket.TransId_Cash,
				ErrorCode: betTicket.ErrorCode,
				Message: betTicket.Message
			};
				
			get().setTicketCarts('normal', { multiTickets: [newTicket] });
			get().checkCanBet('normal', false);
		},
		setIsParlayMode: (value: boolean) => {
			if (value && get().isQuickBet) {
				get().toggleIsQuickBet();
			}

			set({
				isParlayMode: value,
				betslipTab: value ? 'parlay' : get().betslipTab
			});

			const betslipType = get().getBetslipType('normal');
			set({ betslipType });
		},
		setIsBookingBet: (value: boolean) => set({ isBookingBet: value }),
		toggleBookingBet: () => set(state => ({ isBookingBet: !state.isBookingBet })),
		setBookingData: (data: BookingData) => set({ bookingData: data }),
		setIsConfirmBet: (value: boolean) => {
			set({
				isConfirmBet: value,
				...(value === false ? { betAction: BetAction.Default } : {}),
			});
		},
		setBetslipKey: (key: string) => { // 切換 Sports 及 VirtualSports
			const currentKey = get().betslipKey;

			const multiStorage = {
				...get().multiStorage,
				[currentKey]: get().ticketCarts['normal'].multiTickets
			};
			const parlayStorage = {
				...get().parlayStorage,
				[currentKey]: get().ticketCarts['normal'].parlayTicketModel
			};

			const currentMultiTicketModel = multiStorage.get(key) ?? new Array<TicketModel>();
			const currentParlayTicketModel = parlayStorage.get(key) ?? null;

			set({
				betslipKey: key,
				multiStorage,
				parlayStorage,
			});
			get().setTicketCarts('normal', { multiTickets: currentMultiTicketModel, parlayTicketModel: currentParlayTicketModel });
		},
		// 若為bb plus  type 則會加上判斷是否有bb plus ticket 去決定是否關閉bet slip 的 bb plus mode
		// 其他則照舊
		getBetslipType: (type: 'normal' | 'bb-plus') => {
			if (type === 'bb-plus' && get().isBBPlusBetSlipMode) {
				const bbPlusTickets = get().ticketCarts['bb-plus'].multiTickets;
				if (bbPlusTickets.length === 0) {
					get().setIsBBPlusBetSlipMode(false);
				} 
			}
			
			const tickets=  get().ticketCarts['normal'].multiTickets;
			const length = tickets.length;

			let betslipType: BetslipType;

			if (!get().isParlayMode && get().isQuickBet) return 'quickbet';

			switch (length) {
				case 0:
					betslipType = 'empty';
					break;
				case 1:
					betslipType = 'single';
					break;
				default:
					betslipType = 'multi';
					break;
			}

			return betslipType;
		},
		checkCanBet: (type: 'normal' | 'bb-plus', hasClickBet: boolean, doubleBetMsg?: string) => {
			// 內層提示訊息
			const innerMsg: BetMsgModel[] = get().getMsgByTickets(type);

			// 外層提示訊息
			let outerMsg: BetMsgModel[] = [];

			// same match
			const multiTickets = get().ticketCarts[type].multiTickets;
			for (const ticket of multiTickets) {
				const sameMatchTicketCount = multiTickets.filter(t => t.Matchid === ticket.Matchid || t.ParentMatchid === ticket.Matchid).length;
				if (sameMatchTicketCount > 1 && type !== 'bb-plus') {
					outerMsg = BetslipService.addCanBetMsg(outerMsg, BETTING_CODE.SameMatch);
					break;
				}
			}

			// parlay 外層 error (parlay min odds 不顯示)
			const parlayData = get().ticketCarts[type].parlayTicketModel;
			if (parlayData) {
				const parlayErrorCode = parlayData.Code;
				const parlayErrorMessage = parlayData.Message;

				if (parlayErrorCode === BETTING_CODE.DisableBetProblem) {
					outerMsg = BetslipService.addCanBetMsg(outerMsg, parlayErrorCode, parlayErrorMessage);
				}

				const parlayCommonErrorCode = parlayData?.Common?.ErrorCode;
				const parlayCommonErrorMsg = parlayData?.Message ?? parlayData?.Common?.ErrorMsg;

				if (parlayCommonErrorCode) {
					switch (parlayCommonErrorCode) {
						case BETTING_CODE.ParlayMinOdds:
							// do nothing
							break;
						case BETTING_CODE.ParlayComboByPool:
							// BB+ 外層顯示 Common 訊息 (對應 newmobile ParlayBettingMessage)
							if (type === 'bb-plus') {
								outerMsg = BetslipService.addCanBetMsg(outerMsg, parlayCommonErrorCode, parlayCommonErrorMsg);
							}
							break;
						case BETTING_CODE.BlockDoubleBet:
							doubleBetMsg = parlayCommonErrorMsg;
							break;
						case BETTING_CODE.DisabledParlay: {
							// 任一張票有 error code 58 就顯示，要顯示的訊息是在 ticket item 裡面的 Message 無法用 common error msg 
							const disableParlayTicket = parlayData?.Tickets?.find(ticket => ticket.Code === BETTING_CODE.DisabledParlay);
							const disabledParlayMsg = disableParlayTicket?.Message ?? parlayCommonErrorMsg;
							outerMsg = BetslipService.addCanBetMsg(outerMsg, parlayCommonErrorCode, disabledParlayMsg);
							break;
						}
						default:
							outerMsg = BetslipService.addCanBetMsg(outerMsg, parlayCommonErrorCode, parlayCommonErrorMsg);
							break;
					}
				}
			}

			// 是否全部不可下注
			const allTicketCount = multiTickets.length + (parlayData?.comboData.length ?? 0);
			const canNotBetCount = innerMsg.filter(msg => !msg.canBet).length;
			const canBet = allTicketCount !== canNotBetCount;

			// ConfirmBet 提示訊息
			const isConfirmBet = get().isConfirmBet;
			let confirmBetMsg: BetMsgModel[] = [];

			if (hasClickBet) {
				if (doubleBetMsg) {
					document.dispatchEvent(new CustomEvent('show-toast-msg', {
						detail: { msg: doubleBetMsg }
					}));
				}

				// 下注後的提示訊息 ($$ 相關)
				const moneyRelatedCode = [BETTING_CODE.CreditProblem, BETTING_CODE.OverMaxStakePerMatch, BETTING_CODE.OverMaxPayoutPerMatch];
				moneyRelatedCode.forEach(code => {
					const errorTicket = multiTickets.find(ticket => ticket.Code === code);
					let errorMsg;

					if (errorTicket && errorTicket.Message) { // multi has error
						errorMsg = errorTicket.Message;
					} else if (parlayData && parlayData.Code === code) { // parlay has error
						errorMsg = parlayData.Message;
					}

					if (errorMsg) {
						if (isConfirmBet) {
							confirmBetMsg = BetslipService.addCanBetMsg(confirmBetMsg, code, errorMsg, '', false);
						} else {
							document.dispatchEvent(new CustomEvent('show-toast-msg', {
								detail: { msg: errorMsg }
							}));
						}
					}
				});
			}

			const canBetResult = { innerMsg, outerMsg, canBet, confirmBetMsg };
			get().setTicketCarts(type, { canBetResult });
		},
		getMsgByTickets: (type: 'normal' | 'bb-plus') => {
			let msgArray: BetMsgModel[] = [];

			const multiTickets = get().ticketCarts[type].multiTickets;
			const { betslipTab } = get();
			const checkSupportParlay = multiTickets.length > 1;
			multiTickets.map(ticket => {
				// # 1.1 odds change 且正在parlay tab, 需僅顯示Odds change error
				if (ticket.Code === 15 && betslipTab === 'parlay') {
					msgArray = BetslipService.addCanBetMsg(msgArray, -98, '', ticket.key);
				} else if (ticket.Code !== 0) {
					// #1.2 處理 betting error
					msgArray = BetslipService.addCanBetMsg(msgArray, ticket.Code, ticket.Message ?? '', ticket.key);
				}
				if (ticket.minBet > ticket.maxBet) {
					msgArray = BetslipService.addCanBetMsg(msgArray, BETTING_CODE.MinBetGreaterThanMaxBet, '', ticket.key);
				}
				// BB plus ticket 如果有error ticket.HasParlay 會變false 導致多一筆 error code -97，應該要排除
				// #2 再處理是否支援串關
				if (checkSupportParlay && !ticket.HasParlay && type !== 'bb-plus') {
					msgArray = BetslipService.addCanBetMsg(msgArray, BETTING_CODE.NotSupportParlay, '', ticket.key);
				}
			});

			const parlayData = get().ticketCarts[type].parlayTicketModel;
			if (parlayData) {
				// parlay 內層 error
				const parlayTickets = parlayData?.comboData;

				parlayTickets.map(ticket => {
					if (ticket.ErrorCode === 0) return;	// success
					msgArray = BetslipService.addCanBetMsg(msgArray, ticket.ErrorCode, ticket.ErrorMsg, ticket.key);
				});
			}

			// BB Plus：將 -99 的 canBet 改為 true 避免吃到 betby 設定
			if (type === 'bb-plus') {
				msgArray.forEach(msg => {
					if (msg.code === -99) {
						msg.canBet = true;
					}
				});
			}

			return msgArray;
		}, 
		onOddsButtonClick: (type: 'normal' | 'bb-plus', model: OddsTicketModel, oddsKey: string, childTab: ChildTabType, sourceType: BetSourceType, isParlayMode: boolean) => {
			const isConfirmBet = get().isConfirmBet;
			if (isConfirmBet) {
				get().removeAllTicket(type);
				set({
					isConfirmBet: false,
					isBookingBet: false,
					betAction: BetAction.Default,
				});
			}

			const ticket = BetslipService.getTicketModel(model);

			const multiTickets = get().ticketCarts[type].multiTickets;
			// default mode: parent match / child match 視為不同場, 依各自 matchId 判定是否需顯示 same match 提示
			// parlay mode: parent match / child match 視為同場需互斥, 若有則以 parentMatchId 為判定標準
			const matchId = get().isParlayMode && (ticket.SportType === 1 || ticket.SportType === 50) && ticket.ParentMatchid ? ticket.ParentMatchid : ticket.Matchid;
			const arrayWithoutCurrentMatch = multiTickets.filter(ticket => ticket.ParentMatchid ? ticket.ParentMatchid !== matchId : ticket.Matchid !== matchId);
			const arrayWithCurrentMatch = multiTickets.filter(ticket => ticket.ParentMatchid ? ticket.ParentMatchid === matchId : ticket.Matchid === matchId);
			const isParlaySameMatch = isParlayMode && arrayWithCurrentMatch.length > 0;
			
			// 不同投注類型的投注上限錯誤處理
			const bbPlusMultiTickets = get().ticketCarts['bb-plus'].multiTickets;
			if (type === 'bb-plus' && bbPlusService.getUniqueMatchCount([...bbPlusMultiTickets, ticket]) > selectTicketGroupMaxLengthBBPlus) {
				const canBetResult = get().ticketCarts['bb-plus'].canBetResult;
				const outerMsg = canBetResult.outerMsg;

				const key = BETTING_CODE.ExceedLimitSelectionBBPlus;
				const isMsgExist = outerMsg.find(msg => msg.code === key);

				if (!isMsgExist) {
					const updatedOuterMsg = BetslipService.addCanBetMsg(outerMsg, key);
					const updatedCanBetResult = { ...canBetResult, outerMsg: updatedOuterMsg };
					get().setTicketCarts('bb-plus', { canBetResult: updatedCanBetResult });
				}

				return undefined;
			} else if (type === 'normal' && !isParlaySameMatch && multiTickets.length >= selectTicketMaxLength) { // 上限20張
				const canBetResult = get().ticketCarts['normal'].canBetResult;
				const outerMsg = canBetResult.outerMsg;

				const key = BETTING_CODE.ExceedLimitSelection;
				const isMsgExist = outerMsg.find(msg => msg.code === key);

				if (!isMsgExist) {
					const updatedOuterMsg = BetslipService.addCanBetMsg(outerMsg, key);
					const updatedCanBetResult = { ...canBetResult, outerMsg: updatedOuterMsg };
					get().setTicketCarts('normal', { canBetResult: updatedCanBetResult });
				}

				return undefined;
			}
			
			// 不同投注類型票券的處理
			let newTickets;
			if (type === 'bb-plus') {
				// BB+: 防呆 防止same pair odds 被送去打後端
				ticket.Matchid = model.Matchid;
				newTickets = [...bbPlusMultiTickets].filter(newTicket => !(newTicket.Matchid === ticket.Matchid && newTicket.OddsID === ticket.OddsID));
				// bb+ mode: parent match / child match 視為同場, 但grey out api 需要分matchid 以及 parent id 做判斷
				newTickets.push(ticket);
			} else if (isParlaySameMatch) { // 在 parlay 模式會後踢前
				newTickets = [
					...arrayWithoutCurrentMatch,
					ticket
				];
			} else {
				// 相同 matchId 的票要篩選出來一起移到 betslip 最下面
				// If the ticket is from SMP, remove it
				newTickets = [
					...arrayWithoutCurrentMatch,
					...arrayWithCurrentMatch,
					ticket
				].filter(not(smpService.isSmpTicket));
			}
			
			// 不同投注類型決定要存到哪個購物車
			get().setTicketCarts(type, { multiTickets: newTickets });

			if (sportService.isBetbyVirtualSports(ticket.SportType)) {
				get().toggleBetbyTicketKey(oddsKey);
			}

			const betslipType = get().getBetslipType(type);
			set({
				betslipType,
				useSabaCoin: false,
			});

			if (sourceType) {
				const addTicketStorage = get().sourceStorage.get(sourceType) ?? new Map<ChildTabType, Array<string>>();
				const childTabStorage = addTicketStorage.get(childTab) ?? [];
				childTabStorage.push(oddsKey);
				addTicketStorage.set(childTab, childTabStorage);
				get().setSourceStorage(new Map<BetSourceType, Map<ChildTabType, Array<string>>>().set(sourceType, addTicketStorage ?? []));
			}

			return ticket.key;
		},
		onSmpTrigger: (ticket: TicketModel) => {
			set({
				isQuickBet: false,
				betslipType: 'single',
			});
			get().setTicketCarts('normal', { multiTickets: [ticket], parlayTicketModel: null });
			get().switchToUserBetSlipTab('single');
			return '';
		},
		getTotalBet: (type: 'normal' | 'bb-plus', isIndependentMode: boolean, isConfirmBet?: boolean) => {
			const betslipTab = get().betslipTab;
			const canCountSingle = !isIndependentMode || betslipTab === 'single';
			const canCountParlay = !isIndependentMode || betslipTab === 'parlay' || type === 'bb-plus';
			const canBetResult = get().ticketCarts[type].canBetResult;

			let totalBet = 0;

			// bbplus 只需要記錄parlay ticket 的數量
			if (type === 'bb-plus') {
				const parlayData = get().ticketCarts['bb-plus'].parlayTicketModel;
				const parlayTickets = parlayData?.comboData;
				parlayTickets?.filter(ticket => {
					const canBet = (!isConfirmBet && !canBetResult.innerMsg.some(msg => msg.ticketKey == ticket.key && !msg.canBet)) || (isConfirmBet && ticket.DoPlaceBet);
					return ticket.stake > 0 && canBet;
				})?.forEach(ticket => totalBet += ticket.BetCount);
				return totalBet;
			}

			if (canCountSingle) {
				const multiTickets = get().ticketCarts['normal'].multiTickets;
				totalBet += multiTickets.filter(ticket => {
					return ticket.stake > 0 && !canBetResult.innerMsg.some(msg => !isConfirmBet && msg.ticketKey == ticket.key && !msg.canBet);
				}).length;
			}

			if (canCountParlay) {
				const parlayData = get().ticketCarts['normal'].parlayTicketModel;
				const parlayTickets = parlayData?.comboData;
				parlayTickets?.filter(ticket => {
					const canBet = (!isConfirmBet && !canBetResult.innerMsg.some(msg => msg.ticketKey == ticket.key && !msg.canBet)) || (isConfirmBet && ticket.DoPlaceBet);
					return ticket.stake > 0 && canBet;
				})?.forEach(ticket => totalBet += ticket.BetCount);
			}

			return totalBet;
		},
		getTotalStake: (type: 'normal' | 'bb-plus', isIndependentMode: boolean, isConfirmBet?: boolean) => {
			const betslipTab = get().betslipTab;
			const canCountSingle = !isIndependentMode || betslipTab === 'single';
			const canCountParlay = !isIndependentMode || betslipTab === 'parlay';
			const canBetResult = get().ticketCarts[type].canBetResult;

			let totalStake = 0;

			if (canCountSingle) {
				const multiTickets = get().ticketCarts[type].multiTickets;
				multiTickets.filter(ticket => {
					return !canBetResult.innerMsg.some(msg => !isConfirmBet && msg.ticketKey == ticket.key && !msg.canBet);
				}).map(ticket => totalStake += ticket.stake || 0);
			}

			if (canCountParlay) {
				const parlayData = get().ticketCarts[type].parlayTicketModel;
				const parlayTickets = parlayData?.comboData;
				parlayTickets?.filter(ticket => {
					return !canBetResult.innerMsg.some(msg => !isConfirmBet && msg.ticketKey == ticket.key && !msg.canBet);
				})?.map(ticket => totalStake += ticket.stake * ticket.BetCount);
			}

			return totalStake;
		},
		getTotalPayout: (type: 'normal' | 'bb-plus', isIndependentMode: boolean) => {
			const betslipTab = get().betslipTab;
			const canCountSingle = !isIndependentMode || betslipTab === 'single';
			const canCountParlay = !isIndependentMode || betslipTab === 'parlay';
			const canBetResult = get().ticketCarts[type].canBetResult;

			let totalPayout = 0;
			const multiTickets = get().ticketCarts[type].multiTickets;
			const parlayData = get().ticketCarts[type].parlayTicketModel;
			const parlayTickets = parlayData?.comboData;

			// BB plus 只計算parlay ticket
			if (type === 'bb-plus') {
				parlayTickets?.filter(ticket => {
					return !canBetResult.innerMsg.some(msg => msg.ticketKey == ticket.key && !msg.canBet);
				})?.map(ticket => {
					if (ticket.payout) {
						totalPayout += ticket.payout;
					}
				});
				return totalPayout;
			}

			if (canCountSingle) {
				multiTickets.filter(ticket => {
					return !canBetResult.innerMsg.some(msg => msg.ticketKey == ticket.key && !msg.canBet);
				}).map(ticket => {
					if (ticket.payout) {
						totalPayout += ticket.payout;
					}
				});
			}

			if (canCountParlay) {
				parlayTickets?.filter(ticket => {
					return !canBetResult.innerMsg.some(msg => msg.ticketKey == ticket.key && !msg.canBet);
				})?.map(ticket => {
					if (ticket.payout) {
						totalPayout += ticket.payout;
					}
				});
			}

			return totalPayout;
		},
		getTicketByKey: (type: 'normal' | 'bb-plus', key: string, isParlay: boolean) => {
			if (!isParlay) {
				const tickets = get().ticketCarts[type].multiTickets;
				return tickets.find(ticket => ticket.key === key);
			} else {
				const parlayTicketModel = get().ticketCarts[type].parlayTicketModel;
				if (parlayTicketModel) {
					const tickets = parlayTicketModel.comboData;
					return tickets.find(ticket => ticket.key === key);
				}
			}
		},
		updateTicket: (type: 'normal' | 'bb-plus', value: TicketModel | ParlayTicketModel, isParlay: boolean, oddsType?: number) => {
			if (!isParlay) {
				get().updateMultiTicket(type, value as TicketModel, oddsType ?? 0);
			} else {
				get().updateParlayTicket(type, value as ParlayTicketModel);
			}
		},
		updateMultiTicket: (type: 'normal' | 'bb-plus', value: TicketModel, oddsType: number) => {
			value.payout = BetslipService.getPayoutByTicket(value, oddsType, get().useVoucher);

			const tickets = get().ticketCarts[type].multiTickets;
			const index = tickets.findIndex(ticket => ticket.key === value.key);
			const newTickets = [...tickets.slice(0, index), value, ...tickets.slice(index + 1, tickets.length)];

			get().setTicketCarts(type, { multiTickets: newTickets });
		},
		updateParlayItemTicket: (type: 'normal' | 'bb-plus', value: TicketModel) => {
			const parlayTicketModel = get().ticketCarts[type].parlayTicketModel;

			if (parlayTicketModel) {
				const tickets = parlayTicketModel.Tickets;

				// parlay ticket model come from api without key
				const index = tickets.findIndex(ticket => ticket.sinfo === value.sinfo);

				const newTickets = [...tickets.slice(0, index), value, ...tickets.slice(index + 1, tickets.length)];

				const newModel: ParlayTicketsModel = {
					...parlayTicketModel,
					Tickets: newTickets
				};

				get().setTicketCarts(type, { parlayTicketModel: newModel });
			}
		},
		updateParlayTicket: (type: 'normal' | 'bb-plus', value: ParlayTicketModel) => {
			const parlayTicketModel = get().ticketCarts[type].parlayTicketModel;

			if (parlayTicketModel) {
				value.payout = BetslipService.getPayoutByParlayTicket(value);

				const tickets = parlayTicketModel.comboData;
				const index = tickets.findIndex(ticket => ticket.key === value.key);
				const newTickets = [...tickets.slice(0, index), value, ...tickets.slice(index + 1, tickets.length)];

				const newModel: ParlayTicketsModel = {
					...parlayTicketModel,
					comboData: newTickets
				};

				get().setTicketCarts(type, { parlayTicketModel: newModel });
			}
		},
		removeTicket: (type: 'normal' | 'bb-plus', oddsKey: string | undefined, childTab: ChildTabType, sourceType?: BetSourceType) => {
			if (!oddsKey) {
				return;
			}

			const tickets = get().ticketCarts[type].multiTickets;
			const newTickets = tickets.filter(ticket => ticket.key !== oddsKey);
				
			get().setTicketCarts(type, { multiTickets: newTickets });

			const currentTicket = tickets.find(ticket => ticket.key === oddsKey);

			if (currentTicket) {
				if (sportService.isBetbyVirtualSports(currentTicket.SportType)) {
					get().toggleBetbyTicketKey(oddsKey);
				}
			}
			const betslipType = get().getBetslipType(type);
			set({ betslipType });

			if (sourceType) {
				const removeTicketStorage = get().sourceStorage.get(sourceType) ?? new Map<ChildTabType, Array<string>>();
				const removeTicketChildTabStorage = removeTicketStorage?.get(childTab)?.filter(oddskey => oddskey !== oddsKey);
				removeTicketStorage.set(childTab, removeTicketChildTabStorage ?? []);
				get().setSourceStorage(new Map<BetSourceType, Map<ChildTabType, Array<string>>>().set(sourceType, removeTicketStorage));
			} else {
				const removeTicketStorage = get().sourceStorage.get('promotion') ?? new Map<ChildTabType, Array<string>>();
				if (removeTicketStorage.size > 0) {
					let foundInTab: ChildTabType | undefined;
					removeTicketStorage.forEach((values, childTab) => {
						if (values.includes(oddsKey ?? '')) {
							foundInTab = childTab;
						}
					}); 
					if (foundInTab) {
						const removeTicketChildTabStorage = removeTicketStorage?.get(foundInTab)?.filter(oddskey => oddskey !== oddsKey);
						removeTicketStorage.set(foundInTab, removeTicketChildTabStorage ?? []);
						get().setSourceStorage(new Map<BetSourceType, Map<ChildTabType, Array<string>>>().set('promotion', removeTicketStorage));
					}
				}
			}

			// SBRWidget
			if (oddsKey && typeof window !== 'undefined') {
				const iframes = document.querySelectorAll('iframe');

				iframes.forEach((iframe) => {
					const iframeContentWindow = iframe.contentWindow;
					if (iframeContentWindow) {
						const message = {
							eventCode: 'B002',
							selectionKey: oddsKey
						};

						iframeContentWindow.postMessage(message, '*');
					}
				});
			}
		},
		removeAllTicket: (type: 'normal' | 'bb-plus') => {
			const tickets = get().ticketCarts[type].multiTickets;
			tickets.map(ticket => get().removeTicket(type, ticket.key, 'null'));
			type === 'normal' && get().clearBetbyTicketKeys();
			get().setTicketCarts(type, { parlayTicketModel: null });
		},
		removeAllCartTicket: () => {
			get().removeAllTicket('normal');
			get().removeAllTicket('bb-plus');
		},
		setIsExpand: (type: 'normal' | 'bb-plus', value: boolean) => {
			get().setFocusOddsKey(type, '');
			get().setSelectAllOddsKey(type, '');
			set({ isExpand: value });
		},
		toggleIsExpand: (type: 'normal' | 'bb-plus') => {
			get().setFocusOddsKey(type, '');
			get().setSelectAllOddsKey(type, '');
			set({ isExpand: !get().isExpand });
		},
		setIsQuickBet: (value: boolean) => {
			set({ isQuickBet: value });

			const betslipType: BetslipType = get().getBetslipType('normal');
			get().setFocusOddsKey('normal', '');
			get().setSelectAllOddsKey('normal', '');

			set({
				betslipType,
				isQuickbetExpand: true
			});
		},
		setIsQuickBetSettingModalOpen: (value: boolean) => {
			set({ isQuickBetSettingModalOpen: value });
		},
		toggleIsQuickBet: () => {
			set({ isQuickBet: !get().isQuickBet, isBBPlusBetSlipMode: false });

			const betslipType: BetslipType = get().getBetslipType('normal');
			get().setFocusOddsKey('normal', '');
			get().setSelectAllOddsKey('normal', '');

			set({
				betslipType,
				isQuickbetExpand: true
			});
		},
		toggleIsQuickBetExpand: () => {
			const isQuickbetExpand = !get().isQuickbetExpand;
			get().setFocusOddsKey('normal', '');
			get().setSelectAllOddsKey('normal', '');

			set({
				isQuickbetExpand,
			});
		},
		setFocusOddsKey: (type: 'normal' | 'bb-plus', value: string) => {
			get().setTicketCarts(type, { focusOddsKey: value });
		},
		setSelectAllOddsKey: (type: 'normal' | 'bb-plus', value: string) => {
			get().setTicketCarts(type, { selectAllOddsKey: value });
		},
		setQuickBetTicket: (model: OddsTicketModel) => {
			const ticket = BetslipService.getTicketModel(model);

			set({ quickBetTicket: ticket });
			return ticket.key;
		},
		clearQuickBetTicket: () => {
			set({ quickBetTicket: null });
		},
		setMultiStakePerBet: (type: 'normal' | 'bb-plus', value: number) => {
			get().setTicketCarts(type, { multiStakePerBet: value });
		},
		setParlayStakePerBet: (type: 'normal' | 'bb-plus', value: number) => {
			get().setTicketCarts(type, { parlayStakePerBet: value });
		},
		checkStake: (type: 'normal' | 'bb-plus', isIndependentMode: boolean, oddsType: number): boolean => {
			const {
				betslipType,
				betslipTab
			} = get();
			const canBetResult = get().ticketCarts[type].canBetResult;
			const currentMultiTickets = get().ticketCarts[type].multiTickets;
			const currentParlayTicketModel = get().ticketCarts[type].parlayTicketModel;
			const currentParlayTickets = currentParlayTicketModel?.comboData;

			const bettingMultiTickets = currentMultiTickets.filter(ticket => ticket.stake);
			const bettingParlayTickets = currentParlayTickets?.filter(parlayTicket => parlayTicket.stake);

			const shouldCheckSingle = !isIndependentMode || betslipTab === 'single' || betslipType === 'single';
			const shouldCheckParlay = ((!isIndependentMode || betslipTab === 'parlay') && betslipType === 'multi') || type === 'bb-plus';

			let isSuccess = true;

			set({ tooltipMap: new Map() });
			const nextTooltipMap = new Map<string, Record<string, { value?: string | number }>>();

			// 完全沒有填入Stake, 或是填入的stake不是當前分離模式的tab
			if ((!bettingMultiTickets.length || !shouldCheckSingle) && (!bettingParlayTickets?.length || !shouldCheckParlay)) {
				if (betslipType === 'single') {
					const ticket = currentMultiTickets[0];

					nextTooltipMap.set(ticket.key, { 'lbl_MinStakeErrMsg': { value: ticket.minBet } });

					get().updateMultiTicket(type, {
						...ticket,
						stake: ticket.minBet
					}, oddsType);

					isSuccess = false;
				} else if (betslipType === 'multi' || type === 'bb-plus') { // bb plus 也使用 multi 的流程
					// 有parlay票的狀況 //TODO: 當有parlay票但全部不可下注時，應套用multi bet的 流程
					if (currentParlayTickets?.length && shouldCheckParlay) {
						const parlayTicket = currentParlayTickets.find(parlayTicket => parlayTicket.isMixParlay);
						const ticketMsg = canBetResult.innerMsg.find(msg => msg.ticketKey === parlayTicket?.key);
						const canBet = !ticketMsg || (ticketMsg && ticketMsg.canBet);

						if (canBet) {
							nextTooltipMap.set((parlayTicket as ParlayTicketModel).key, { 'lbl_MinStakeErrMsg': { value: currentParlayTicketModel?.MinBet } });

							get().updateParlayTicket(type, {
								...parlayTicket,
								stake: Number(removeCommas(currentParlayTicketModel?.MinBet ?? ''))
							} as ParlayTicketModel);
						}

						isSuccess = false;
					} else {
						currentMultiTickets.forEach(ticket => {
							const ticketMsg = canBetResult.innerMsg.find(msg => msg.ticketKey === ticket?.key);
							const canBet = !ticketMsg || (ticketMsg && ticketMsg.canBet);

							if (canBet) {
								nextTooltipMap.set(ticket.key, { 'lbl_MinStakeErrMsg': { value: ticket.minBet } });

								get().updateMultiTicket(type, {
									...ticket,
									stake: ticket.minBet
								}, oddsType);
							}
						});

						isSuccess = false;
					}
				}
			} else {
				// 檢測所有有填stake 的ticket
				if (shouldCheckSingle) {
					bettingMultiTickets.forEach(ticket => {
						if (ticket.stake > ticket.maxBet) {
							isSuccess = false;
							nextTooltipMap.set(ticket.key, { 'lbl_MaxStakeErrMsg': { value: ticket.maxBet } });

							get().updateMultiTicket(type, {
								...ticket,
								stake: ticket.maxBet
							}, oddsType);
						} else if (ticket.stake < ticket.minBet) {
							isSuccess = false;
							nextTooltipMap.set(ticket.key, { 'lbl_MinStakeErrMsg': { value: ticket.minBet } });

							get().updateMultiTicket(type, {
								...ticket,
								stake: ticket.minBet
							}, oddsType);
						}
					});
				}

				if (shouldCheckParlay) {
					bettingParlayTickets?.forEach(parlayTicket => {
						if (parlayTicket.stake > parlayTicket.MaxBet) {
							isSuccess = false;
							nextTooltipMap.set(parlayTicket.key, { 'lbl_MaxStakeErrMsg': { value: parlayTicket.MaxBet } });

							get().updateParlayTicket(type, {
								...parlayTicket,
								stake: parlayTicket.MaxBet
							});
						} else if (parlayTicket.stake < Number(removeCommas(currentParlayTicketModel?.MinBet ?? ''))) {
							isSuccess = false;
							nextTooltipMap.set(parlayTicket.key, { 'lbl_MinStakeErrMsg': { value: currentParlayTicketModel?.MinBet } });

							get().updateParlayTicket(type, {
								...parlayTicket,
								stake: Number(removeCommas((currentParlayTicketModel?.MinBet ?? '')))
							});
						}
					});
				}
			}

			set({ tooltipMap: nextTooltipMap });
			return isSuccess;
		},
		betAgain: (type: 'normal' | 'bb-plus', oddsType: number, defaultStakeId: string, InpbetStake: string, view: number) => {
			const currentMultiTickets = get().ticketCarts[type].multiTickets;
			const currentParlayTicketModel = get().ticketCarts[type].parlayTicketModel;
			const stake = parseInt(defaultStakeId) === BetDefaultStakeType.Customize && InpbetStake ? parseInt(InpbetStake) : 0;
			currentMultiTickets.forEach(ticket => {
				get().updateMultiTicket(type, {
					...ticket,
					stake: stake === 0 && parseInt(defaultStakeId) === BetDefaultStakeType.LastBet ? ticket.stake : stake,
					ErrorCode: 0,
					Message: '',
					TransId: '',
					Common: {
						ErrorCode: 0,
						ErrorMsg: ''
					}
				}, oddsType);
			});

			currentParlayTicketModel?.comboData?.forEach(parlayTicket => {
				if (parlayTicket.isMixParlay) {
					get().updateParlayTicket(type, {
						...parlayTicket,
						stake: view === ViewEnum.galaxy ? (defaultStakeId === '0' ? parlayTicket.stake : 0) : (stake === 0 && parseInt(defaultStakeId) === BetDefaultStakeType.LastBet) ? parlayTicket.stake : stake,
						ErrorCode: 0,
						ErrorMsg: ''
					});
				} else {
					get().updateParlayTicket(type, {
						...parlayTicket,
						stake: 0,
						ErrorCode: 0,
						ErrorMsg: ''
					});
				}
			})

			// reset UI state for both carts (normal + bb-plus)
			get().setMultiStakePerBet('normal', 0);
			get().setParlayStakePerBet('normal', 0);
			get().setMultiStakePerBet('bb-plus', 0);
			get().setParlayStakePerBet('bb-plus', 0);
			get().setSelectAllOddsKey('normal', '');
			get().setFocusOddsKey('normal', '');
			get().setSelectAllOddsKey('bb-plus', '');
			get().setFocusOddsKey('bb-plus', '');

			set({
				isConfirmBet: false,
				isBookingBet: false,
				useSabaCoin: false,
				betAction: BetAction.BetAgain
			});
		},
		setVoucher: (voucherType: VoucherType) => {
			set({
				useVoucher: voucherType,
				...(voucherType !== 'FreeBet'
					? { selectedFreeBetVoucherQuota: null, selectedFreeBetVoucherId: null }
					: {}),
			});
		},
		removeFreeBetForInvalidOdds: () => {
			const { selectedFreeBetVoucherId } = get();
			set({
				useVoucher: 'None',
				selectedFreeBetVoucherQuota: null,
				selectedFreeBetVoucherId: null,
				freeBetRemovedByInvalidOddsTooltipOpen: true,
				preFreebetVoucherId: selectedFreeBetVoucherId
			});
		},
		setFreeBetRemovedByInvalidOddsTooltipOpen: (open: boolean) =>
			set({ freeBetRemovedByInvalidOddsTooltipOpen: open }),
		setShowFreeBetOddsConversionTooltip: (show: boolean) =>
			set({ showFreeBetOddsConversionTooltip: show }),
		setSelectedFreeBetVoucher: (quota: number, voucherId: string | null, voucherSystemVersion: number) => {
			set({
				useVoucher: 'FreeBet',
				selectedFreeBetVoucherQuota: quota,
				selectedFreeBetVoucherId: voucherId,
				selectedFreeBetVoucherSystemVersion: voucherSystemVersion,
			});
		},
		setSelectedFreeBetCondition: (condition: FreeBetVoucherConditionForValidation | null) => {
			set({ selectedFreeBetCondition: condition });
		},
		setUseFreeRiskFreeVocher: (value: boolean) => set({ useFreeRiskFreeVoucher: value }),
		setUseSabaCoin: (value: boolean) => set({ useSabaCoin: value }),
		setBetAction: (betAction: number) => {
			set({ betAction });
		},
		setSourceStorage: (sourceStorage: Map<BetSourceType, Map<ChildTabType, Array<string>>>) => {
			set({ sourceStorage: new Map(sourceStorage) });
		},
		setEnableQuickBet: (value: boolean) => {
			set({ enableQuickBet: value });
		},
		setBetslipTab: (value: NormalBetslipTab) => {
			set({ betslipTab: value });
		},
		setBetTimer: (value: number) => {
			set({ betTimer: value });
		},
		toggleBetbyTicketKey: (oddsKey: string) => {
			const betbyTicketKeys = get().betbyTicketKeys;
			const newSet = new Set(betbyTicketKeys);

			if (newSet.has(oddsKey)) {
				newSet.delete(oddsKey);
			} else {
				newSet.add(oddsKey);
			}

			set({ betbyTicketKeys: newSet });
		},
		clearBetbyTicketKeys: () => {
			set({ betbyTicketKeys: new Set() });
		},
		// 當user點擊上方tab時，切換投注單tab, smp也會強制換回single
		switchToUserBetSlipTab: (tabs: UserBetSlipTab) => {
			switch (tabs) {
				case 'single':
					set({ betslipTab: 'single', isBBPlusBetSlipMode: false });
					break;
				case 'parlay':
					set({ betslipTab: 'parlay', isBBPlusBetSlipMode: false });
					break;
				case 'bb-plus':
					set({ isBBPlusBetSlipMode: true });
					break;
				case 'standard':
					set({ betslipTab: 'single', isBBPlusBetSlipMode: false });
					break;
				default:
					break;
			}
		}
	}))
);